/* O painel de sincronização — a primeira coisa nesta SPA que faz polling.
 *
 * O laço só existe enquanto há corrida em andamento: `refetchInterval` é uma FUNÇÃO do
 * último dado, então volta `false` quando nada está rodando e a página fica tão estática
 * quanto o resto. Parado, zero tráfego de fundo.
 *
 * `staleTime: 0` aqui é obrigatório e fácil de perder: o default global é 60 s, e ele
 * serviria um `running: false` velho — o laço nunca começaria depois de apertar o botão.
 *
 * A fase vem do servidor, não da mutation. A mutation resolve em ~50 ms e o trabalho
 * dura ~60 s; e com APP_WORKERS=2 o polling cai no worker que NÃO está sincronizando
 * metade das vezes, e é por isso que o progresso mora em state.sqlite. */

import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "../lib/api";
import { fmtDateTime, intBR } from "../lib/format";
import { Card } from "./ui";

export interface SyncRun {
  id: number; started_at: string; finished_at: string | null;
  status: "running" | "ok" | "noop" | "refused" | "failed";
  trigger: string; phase: string | null;
  pages: number; rows_read: number;
  remote_rows: number | null; mirror_rows: number | null;
  inserted: number | null; updated: number | null; deleted: number | null;
  reclassified: number | null; unclassified: number | null;
  bytes: number | null; sha256: string | null; duration_ms: number | null;
  rebuilt: number; detail: string | null;
}

export interface SyncStatus {
  configured: boolean; scheduled: boolean; scheduled_at: string | null;
  next_run_at: string | null; running: boolean;
  mirror: { present: boolean; active: boolean; bytes?: number; modified_at?: string };
  last: SyncRun | null; last_success: SyncRun | null;
}

const STATUS_LABEL: Record<string, string> = {
  running: "em andamento", ok: "aplicado", noop: "nada mudou",
  refused: "recusado", failed: "falhou",
};

export function useSyncStatus() {
  return useQuery({
    queryKey: ["sync-status"],
    queryFn: () => api<SyncStatus>("/api/sync/status"),
    refetchInterval: (query) => (query.state.data?.running ? 2000 : false),
    staleTime: 0,
  });
}

export function SyncPanel() {
  const client = useQueryClient();
  const status = useSyncStatus();
  const [error, setError] = useState<string | null>(null);
  const wasRunning = useRef(false);

  // Na borda de rodando -> parado, tudo que veio do dashboard ficou velho: um sync
  // reconstrói o dashboard inteiro. Menos os dados dos agentes: o sync não os toca,
  // e as queries deles usam staleTime Infinity de propósito — invalidá-las aqui
  // refaria ~1 MB de rede à toa a cada sync. Elas só mudam pelo "Atualizar dados".
  useEffect(() => {
    const running = status.data?.running ?? false;
    if (wasRunning.current && !running) {
      client.invalidateQueries({
        predicate: (q) => !["reports-latest", "margin"].includes(String(q.queryKey[0])),
      });
    }
    wasRunning.current = running;
  }, [status.data?.running, client]);

  const run = useMutation({
    mutationFn: () => api<{ started: boolean }>("/api/sync/run", { json: {} }),
    onMutate: () => setError(null),
    onSuccess: () => client.invalidateQueries({ queryKey: ["sync-status"] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const data = status.data;
  const running = data?.running ?? false;
  const last = data?.last;

  const label = run.isPending ? "enviando…"
    : running ? (last?.phase ?? "sincronizando…")
    : "sincronizar agora";

  return (
    <Card title="Sincronização com o Supabase"
          sub={data?.scheduled
            ? `automática às ${data.scheduled_at}, e sob demanda`
            : "somente sob demanda (agendamento desligado)"}>
      <div className="controls" style={{ marginBottom: 14 }}>
        <button className="btn primary"
                disabled={running || run.isPending || !data?.configured}
                onClick={() => run.mutate()}>
          {label}
        </button>
        {/* A região viva anuncia só a MUDANÇA de fase. O contador de página fica
            fora dela, senão um leitor de tela lê 78 atualizações seguidas. */}
        <span role="status" aria-live="polite" className="mono" style={{ fontSize: 12 }}>
          {running ? (last?.phase ?? "") : ""}
        </span>
        {running && last && last.pages > 0 && (
          <span aria-hidden className="mono" style={{ fontSize: 12, color: "var(--ink-3)" }}>
            {intBR(last.rows_read)} linhas
          </span>
        )}
      </div>

      {!data?.configured && (
        <div className="banner" role="status">
          <span aria-hidden>⚠</span>
          <span>
            <code>SUPABASE_URL</code> e <code>SUPABASE_KEY</code> não estão definidas
            neste servidor. Sem elas não há de onde sincronizar — o painel segue lendo o
            snapshot congelado.
          </span>
        </div>
      )}

      {error && (
        <div className="banner" role="alert">
          <span aria-hidden>⚠</span><span>{error}</span>
        </div>
      )}

      {last && !running && (
        <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 18px",
                     margin: 0, fontSize: 13 }}>
          <dt>último</dt>
          <dd>
            {fmtDateTime(last.finished_at ?? last.started_at)} ·{" "}
            <span className={`tag${last.status === "failed" || last.status === "refused"
              ? " alta" : ""}`}>
              {STATUS_LABEL[last.status] ?? last.status}
            </span>{" "}
            <span style={{ color: "var(--ink-3)" }}>({last.trigger})</span>
          </dd>

          {last.status === "ok" && (
            <>
              <dt>mudanças</dt>
              <dd>
                {intBR(last.inserted ?? 0)} novas · {intBR(last.updated ?? 0)} alteradas ·{" "}
                {intBR(last.deleted ?? 0)} removidas
                {(last.reclassified ?? 0) > 0 && (
                  <> · <strong>{intBR(last.reclassified!)} reclassificadas</strong></>
                )}
              </dd>
              <dt>espelho</dt>
              <dd>
                {intBR(last.mirror_rows ?? 0)} linhas ·{" "}
                <code>{(last.sha256 ?? "").slice(0, 12)}</code>
                {last.rebuilt ? " · painel reconstruído" : " · painel NÃO reconstruído"}
              </dd>
            </>
          )}

          {(last.status === "refused" || last.status === "failed") && last.detail && (
            <>
              <dt>motivo</dt>
              <dd style={{ whiteSpace: "pre-wrap" }}>{last.detail}</dd>
            </>
          )}

          {last.duration_ms != null && (
            <>
              <dt>duração</dt>
              <dd>{(last.duration_ms / 1000).toFixed(1)} s</dd>
            </>
          )}
        </dl>
      )}

      {!last && (
        <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 13 }}>
          Nenhuma sincronização registrada ainda. O painel está lendo o snapshot
          congelado de <code>input/</code>.
        </p>
      )}
    </Card>
  );
}
